import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface CompareInfluencer {
  id: string;
  name: string;
  primary_handle: string;
  profile_pic_path?: string | null;
  total_followers_count?: number | null;
  avg_engagement_rate?: number | null;
  tier?: string | null;
  city?: string | null;
  country?: string | null;
  categories?: string[] | null;
  platforms?: Record<string, any> | null;
  quality_score?: number | null;
}

interface CompareContextType {
  selectedInfluencers: CompareInfluencer[];
  addToCompare: (influencer: CompareInfluencer) => boolean;
  removeFromCompare: (id: string) => void;
  clearCompare: () => void;
  isSelected: (id: string) => boolean;
  canAddMore: boolean;
  isCompareOpen: boolean;
  setIsCompareOpen: (open: boolean) => void;
  MIN_TO_COMPARE: number;
  MAX_TO_COMPARE: number;
}

const MIN_TO_COMPARE = 2;
const MAX_TO_COMPARE = 4;

const CompareContext = createContext<CompareContextType | undefined>(undefined);

export const CompareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedInfluencers, setSelectedInfluencers] = useState<CompareInfluencer[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);

  const canAddMore = selectedInfluencers.length < MAX_TO_COMPARE;

  const isSelected = useCallback((id: string) => {
    return selectedInfluencers.some(inf => inf.id === id);
  }, [selectedInfluencers]);

  const addToCompare = useCallback((influencer: CompareInfluencer): boolean => {
    // Prevent duplicates
    if (isSelected(influencer.id)) {
      return false;
    }
    
    // Prevent exceeding max
    if (selectedInfluencers.length >= MAX_TO_COMPARE) {
      return false;
    }

    setSelectedInfluencers(prev => [...prev, influencer]);
    return true;
  }, [isSelected, selectedInfluencers.length]);

  const removeFromCompare = useCallback((id: string) => {
    setSelectedInfluencers(prev => prev.filter(inf => inf.id !== id));
  }, []);

  const clearCompare = useCallback(() => {
    setSelectedInfluencers([]);
    setIsCompareOpen(false);
  }, []);

  const value = useMemo(() => ({
    selectedInfluencers,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isSelected,
    canAddMore,
    isCompareOpen,
    setIsCompareOpen,
    MIN_TO_COMPARE,
    MAX_TO_COMPARE,
  }), [
    selectedInfluencers,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isSelected,
    canAddMore,
    isCompareOpen,
  ]);

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (context === undefined) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
